<template>
	<div>
		<!-- Page Header -->
		<JuniperPageHeader back="/juniper" title="Debugging" subtitle="Investigation logs — raw data captured at the moment things ran" />

		<!-- Debugging Section (mirrors juniper/clients layout) -->
		<div class="debug-section mb-4">
			<!-- Filter Tabs Container -->
			<div class="filter-tabs-container">
				<div class="filter-tabs-group">
					<!-- Level Filters -->
					<div class="filter-tabs">
						<button class="filter-tab" :class="{ active: filters.level === '' }" @click="setFilter('level', '')">
							<span class="filter-tab-text">All</span>
						</button>
						<button
							v-for="level in ['info', 'warning', 'error']"
							:key="level"
							class="filter-tab"
							:class="{ active: filters.level === level }"
							@click="setFilter('level', level)"
						>
							<span class="filter-tab-text">{{ level.charAt(0).toUpperCase() + level.slice(1) }}</span>
						</button>
					</div>

					<!-- Divider -->
					<div class="tabs-divider"></div>

					<!-- Channel Filters -->
					<div class="filter-tabs">
						<button class="filter-tab" :class="{ active: filters.channel === '' }" @click="setFilter('channel', '')">
							<span class="filter-tab-text">All channels</span>
						</button>
						<button
							v-for="c in channelOptions"
							:key="c"
							class="filter-tab"
							:class="{ active: filters.channel === c }"
							@click="setFilter('channel', c)"
						>
							<span class="filter-tab-text">{{ c }}</span>
						</button>
					</div>
				</div>

				<!-- Search + actions -->
				<div class="toolbar-right">
					<div class="search-input-wrapper">
						<font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
						<input
							type="text"
							class="search-input"
							placeholder="Search title (press Enter)"
							v-model.trim="filters.search"
							@keyup.enter="applyFilters"
						/>
						<button v-if="filters.search" type="button" class="search-clear-btn" aria-label="Clear search" @click="clearSearch">
							<font-awesome-icon :icon="['fas', 'times']" />
						</button>
					</div>
					<button class="toolbar-icon-btn" title="Debug log settings" @click="openConfig">
						<font-awesome-icon :icon="['fas', 'cog']" />
					</button>
					<button class="toolbar-icon-btn toolbar-icon-btn--danger" title="Delete all logs" :disabled="!meta.total" @click="askDeleteAll">
						<font-awesome-icon :icon="['fas', 'trash']" />
					</button>
				</div>
			</div>

			<!-- Debuggings Table -->
			<div class="debuggings-table">
				<!-- Table Header -->
				<div class="table-header">
					<div class="header-cell">Time</div>
					<div class="header-cell">Client</div>
					<div class="header-cell">Channel</div>
					<div class="header-cell">Action</div>
					<div class="header-cell">Source</div>
					<div class="header-cell">Level</div>
					<div class="header-cell">Title</div>
					<div class="header-cell text-end">Items</div>
					<div class="header-cell text-end">Duration</div>
					<div class="header-cell"></div>
				</div>

				<!-- Body: rows stay mounted (blurred) while loading -->
				<div class="table-body-wrap" :class="{ 'is-loading': loading }">
					<div class="table-body" :class="{ 'is-blurred': loading }">
						<!-- Empty State -->
						<div v-if="!loading && rows.length === 0" class="empty-state">
							<div class="empty-state-icon">
								<font-awesome-icon :icon="['fas', 'bug']" />
							</div>
							<h3 class="empty-state-title">No Debug Records</h3>
							<p class="empty-state-description">
								{{ hasActiveFilters ? 'Try adjusting your filters to see more records' : 'Enable debug toggles via the gear icon — records appear when instrumented flows run (pruned after 7 days)' }}
							</p>
						</div>

						<!-- Table Rows -->
						<div v-for="row in rows" :key="row.id" class="table-row" @click="openDetails(row)">
							<div class="table-cell time-cell">{{ formatDate(row.created_at) }}</div>
							<div class="table-cell client-cell">
								<span v-if="row.client" class="client-name-text">{{ row.client.name }}</span>
								<span v-else class="cell-muted">—</span>
							</div>
							<div class="table-cell">
								<span class="channel-badge">{{ row.channel }}</span>
							</div>
							<div class="table-cell">{{ row.action || '—' }}</div>
							<div class="table-cell">{{ row.source || '—' }}</div>
							<div class="table-cell">
								<span class="level-badge" :class="row.level">{{ row.level }}</span>
							</div>
							<div class="table-cell title-cell" :title="row.title">{{ row.title }}</div>
							<div class="table-cell text-end">{{ row.items_count != null ? row.items_count : '—' }}</div>
							<div class="table-cell text-end">{{ row.duration_ms != null ? row.duration_ms + ' ms' : '—' }}</div>
							<div class="table-cell text-end">
								<button class="row-delete-btn" title="Delete log" @click.stop="askDeleteRow(row)">
									<font-awesome-icon :icon="['fas', 'trash']" />
								</button>
							</div>
						</div>
					</div>

					<div v-if="loading" class="table-loading">
						<div class="table-loading__box">
							<span class="spinner-border spinner-border-sm me-2" role="status"></span>
							Loading debug records…
						</div>
					</div>
				</div>

				<!-- Pagination -->
				<div v-if="meta.last_page > 1" class="jdbg-pagination">
					<button class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page <= 1 || loading" @click="loadPage(meta.current_page - 1)">
						<font-awesome-icon :icon="['fas', 'chevron-left']" />
					</button>
					<span class="jdbg-page-info">Page {{ meta.current_page }} / {{ meta.last_page }} · {{ meta.total }} records</span>
					<button class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page || loading" @click="loadPage(meta.current_page + 1)">
						<font-awesome-icon :icon="['fas', 'chevron-right']" />
					</button>
				</div>
			</div>
		</div>

		<!-- Details modal -->
		<div v-if="details.open" class="jdbg-modal-backdrop" @click.self="closeDetails">
			<div class="jdbg-modal">
				<div class="jdbg-modal-head">
					<div>
						<span class="level-badge me-2" :class="details.row.level">{{ details.row.level }}</span>
						<b>{{ details.row.title }}</b>
						<div class="jdbg-modal-sub">
							#{{ details.row.id }} · {{ formatDate(details.row.created_at) }}
							<span v-if="details.row.client"> · {{ details.row.client.name }}</span>
							<span v-if="details.row.reference_type"> · {{ details.row.reference_type }} #{{ details.row.reference_id }}</span>
						</div>
					</div>
					<button class="btn-close" @click="closeDetails"></button>
				</div>

				<div class="jdbg-modal-body">
					<div v-if="details.loading" class="text-center py-4 cell-muted">Loading payload…</div>
					<template v-else>
						<!-- Summary -->
						<h6 class="jdbg-section-title">Summary</h6>
						<pre class="jdbg-pre">{{ pretty(details.full.summary) }}</pre>

						<!-- Response / payload -->
						<h6 class="jdbg-section-title">
							Payload
							<button class="btn btn-sm btn-outline-secondary ms-2" @click="copyPayload">
								<font-awesome-icon :icon="['fas', 'save']" class="me-1" />
								{{ copied ? 'Copied!' : 'Copy JSON' }}
							</button>
						</h6>
						<pre class="jdbg-pre jdbg-scroll">{{ pretty(details.full.payload) }}</pre>
					</template>
				</div>
			</div>
		</div>

		<!-- Config modal (debug toggles) -->
		<div v-if="config.open" class="jdbg-modal-backdrop" @click.self="closeConfig">
			<div class="jdbg-modal jdbg-modal--narrow">
				<div class="jdbg-modal-head">
					<div>
						<b>Debug logging</b>
						<div class="jdbg-modal-sub">Enabled toggles write a debuggings row for every matching request</div>
					</div>
					<button class="btn-close" @click="closeConfig"></button>
				</div>
				<div class="jdbg-modal-body">
					<div v-if="config.loading" class="text-center py-4 cell-muted">Loading settings…</div>
					<template v-else>
						<div v-for="(actions, channel) in config.toggles" :key="channel" class="config-channel">
							<h6 class="jdbg-section-title">{{ channel }}</h6>
							<div v-for="(item, key) in actions" :key="key" class="form-check form-switch config-switch">
								<input
									class="form-check-input"
									type="checkbox"
									role="switch"
									:id="'toggle-' + channel + '-' + key"
									v-model="item.enabled"
								/>
								<label class="form-check-label" :for="'toggle-' + channel + '-' + key">
									{{ item.label }}
									<code class="config-key">{{ key }}</code>
								</label>
							</div>
						</div>
					</template>
				</div>
				<div class="jdbg-modal-foot">
					<button class="btn btn-sm btn-outline-secondary" @click="closeConfig">Cancel</button>
					<button class="btn btn-sm btn-primary" :disabled="config.saving || config.loading" @click="saveConfig">
						{{ config.saving ? 'Saving…' : 'Save' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Confirm modal (delete) -->
		<div v-if="confirm.open" class="jdbg-modal-backdrop" @click.self="closeConfirm">
			<div class="jdbg-modal jdbg-modal--confirm">
				<div class="jdbg-modal-body">
					<p class="confirm-message">{{ confirm.message }}</p>
				</div>
				<div class="jdbg-modal-foot">
					<button class="btn btn-sm btn-outline-secondary" @click="closeConfirm">Cancel</button>
					<button class="btn btn-sm btn-danger" :disabled="deleting" @click="runConfirm">
						{{ deleting ? 'Deleting…' : 'Delete' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'

export default {
	name: 'JuniperDebugging',
	data() {
		return {
			loading: false,
			rows: [],
			meta: { current_page: 1, last_page: 1, total: 0 },
			channelOptions: [],
			filters: {
				channel: '',
				level: '',
				source: '',
				search: '',
			},
			details: {
				open: false,
				loading: false,
				row: null,
				full: null,
			},
			config: {
				open: false,
				loading: false,
				saving: false,
				toggles: {},
			},
			confirm: {
				open: false,
				message: '',
				action: null,
			},
			deleting: false,
			copied: false,
		}
	},
	computed: {
		...mapState(['authUser']),
		// Same gate as the sidebar item — direct URL access shouldn't bypass it.
		canView() {
			const email = (this.authUser && this.authUser.email) || ''
			return email.startsWith('srdjan') || email.startsWith('marko')
		},
		hasActiveFilters() {
			return !!(this.filters.channel || this.filters.level || this.filters.source || this.filters.search)
		},
	},
	created() {
		if (!this.canView) {
			this.$router.replace('/juniper')
			return
		}
		this.loadPage(1)
	},
	methods: {
		async loadPage(page) {
			this.loading = true
			try {
				const params = { page, per_page: 25 }
				for (const key of ['channel', 'level', 'source', 'search']) {
					if (this.filters[key]) params[key] = this.filters[key]
				}
				const { data } = await juniperApi.get('/debuggings', { params })
				this.rows = data.data || []
				this.meta = {
					current_page: data.current_page,
					last_page: data.last_page,
					total: data.total,
				}
				// Backend ships the distinct channel list, so new channels
				// show up as filter tabs without a frontend change.
				if (Array.isArray(data.channels)) {
					this.channelOptions = data.channels
				}
			} catch (e) {
				this.rows = []
			} finally {
				this.loading = false
			}
		},
		setFilter(key, value) {
			this.filters[key] = value
			this.loadPage(1)
		},
		applyFilters() {
			this.loadPage(1)
		},
		clearSearch() {
			this.filters.search = ''
			this.loadPage(1)
		},
		async openDetails(row) {
			this.details = { open: true, loading: true, row, full: null }
			this.copied = false
			try {
				const { data } = await juniperApi.get('/debuggings/' + row.id)
				this.details.full = data
			} catch (e) {
				this.details.full = { summary: { error: 'Failed to load details' }, payload: {} }
			} finally {
				this.details.loading = false
			}
		},
		closeDetails() {
			this.details = { open: false, loading: false, row: null, full: null }
		},
		// ── Config modal ──────────────────────────────────────────────
		async openConfig() {
			this.config.open = true
			this.config.loading = true
			try {
				const { data } = await juniperApi.get('/debuggings/toggles')
				this.config.toggles = data.toggles || {}
			} catch (e) {
				this.config.toggles = {}
			} finally {
				this.config.loading = false
			}
		},
		closeConfig() {
			this.config.open = false
		},
		async saveConfig() {
			this.config.saving = true
			try {
				// Strip labels — the API expects { channel: { key: bool } }.
				const payload = {}
				for (const [channel, actions] of Object.entries(this.config.toggles)) {
					payload[channel] = {}
					for (const [key, item] of Object.entries(actions)) {
						payload[channel][key] = !!item.enabled
					}
				}
				await juniperApi.put('/debuggings/toggles', { toggles: payload })
				this.config.open = false
			} catch (e) {
				// keep the modal open so nothing is silently lost
			} finally {
				this.config.saving = false
			}
		},
		// ── Delete ────────────────────────────────────────────────────
		askDeleteRow(row) {
			this.confirm = {
				open: true,
				message: `Delete log #${row.id} (${row.title})?`,
				action: async () => {
					await juniperApi.delete('/debuggings/' + row.id)
					this.loadPage(this.meta.current_page)
				},
			}
		},
		askDeleteAll() {
			this.confirm = {
				open: true,
				message: `Delete ALL ${this.meta.total} debug records? This cannot be undone.`,
				action: async () => {
					await juniperApi.delete('/debuggings')
					this.loadPage(1)
				},
			}
		},
		closeConfirm() {
			this.confirm = { open: false, message: '', action: null }
		},
		async runConfirm() {
			if (!this.confirm.action) return
			this.deleting = true
			try {
				await this.confirm.action()
			} catch (e) {
				// list reload happens inside the action; nothing else to do
			} finally {
				this.deleting = false
				this.closeConfirm()
			}
		},
		// ── Misc ──────────────────────────────────────────────────────
		formatDate(value) {
			if (!value) return '—'
			const d = new Date(value)
			if (isNaN(d.getTime())) return value
			return d.toLocaleString()
		},
		pretty(value) {
			try {
				return JSON.stringify(value, null, 2)
			} catch (e) {
				return String(value)
			}
		},
		async copyPayload() {
			try {
				await navigator.clipboard.writeText(this.pretty(this.details.full && this.details.full.payload))
				this.copied = true
				setTimeout(() => (this.copied = false), 2000)
			} catch (e) {
				// Clipboard unavailable (http origin) — ignore.
			}
		},
	},
}
</script>

<style scoped>
/* Developer font for all table/log content on this view */
.debug-section,
.jdbg-modal {
	--jdbg-mono: 'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace;
}

/* Debug Section — same white card language as .clients-section */
.debug-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

/* Filter Tabs Container — mirrors juniper/clients */
.filter-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	padding: 24px;
}

.filter-tabs-group {
	display: flex;
	align-items: center;
	gap: 0;
	flex-wrap: wrap;
}

.filter-tabs {
	display: flex;
	gap: 0;
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.filter-tab {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-tab.active {
	background-color: #ffffff;
	color: #111827;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.filter-tab:hover:not(.active) {
	color: #374151;
}

.filter-tab-text {
	font-weight: 500;
}

.tabs-divider {
	width: 1px;
	height: 24px;
	background-color: #dee2e6;
	margin: 0 8px;
	align-self: center;
}

/* Toolbar right: search + icon buttons */
.toolbar-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.toolbar-icon-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	background: white;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s ease;
}

.toolbar-icon-btn:hover {
	color: #111827;
	border-color: #9ca3af;
}

.toolbar-icon-btn--danger:hover {
	color: #b91c1c;
	border-color: #fca5a5;
}

.toolbar-icon-btn:disabled {
	opacity: 0.4;
	cursor: default;
}

/* Search Input — mirrors juniper/clients */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 32px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-clear-btn {
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #e5e7eb;
	color: #6b7280;
	font-size: 11px;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.search-clear-btn:hover {
	background: #d1d5db;
	color: #1f2937;
}

/* Debuggings Table — full width, mirrors .clients-table grid layout */
.debuggings-table {
	border-top: 1px solid #f3f4f6;
	width: 100%;
}

.table-header {
	display: grid;
	grid-template-columns: 200px 1.2fr 130px 110px 80px 90px 2fr 70px 90px 44px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-family: var(--jdbg-mono);
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.table-row {
	display: grid;
	grid-template-columns: 200px 1.2fr 130px 110px 80px 90px 2fr 70px 90px 44px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
	cursor: pointer;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-family: var(--jdbg-mono);
	font-size: 13px;
	color: #111827;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.time-cell,
.cell-muted {
	color: #6b7280;
}

.client-name-text {
	font-weight: 500;
}

.title-cell {
	color: #374151;
}

/* Row delete button — subtle until hover */
.row-delete-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: #d1d5db;
	cursor: pointer;
	transition: all 0.15s ease;
}

.table-row:hover .row-delete-btn {
	color: #9ca3af;
}

.row-delete-btn:hover {
	background: #fee2e2;
	color: #b91c1c;
}

/* Channel badge — pill like the clients type-badge */
.channel-badge {
	font-family: var(--jdbg-mono);
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.03em;
	display: inline-block;
	background-color: #e0e7ff;
	color: #4338ca;
}

/* Level badge — pill palette matching clients status-badge */
.level-badge {
	font-family: var(--jdbg-mono);
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
	display: inline-block;
}

.level-badge.info {
	background-color: #dbeafe;
	color: #1e40af;
}

.level-badge.warning {
	background-color: #fef3c7;
	color: #92400e;
}

.level-badge.error {
	background-color: #fee2e2;
	color: #991b1b;
}

/* Loading — same pattern as clients: rows stay mounted, blurred */
.table-body-wrap {
	position: relative;
}

.table-body.is-blurred {
	filter: blur(2px);
	pointer-events: none;
	user-select: none;
}

.table-loading {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.table-loading__box {
	display: inline-flex;
	align-items: center;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	padding: 10px 16px;
	font-family: var(--jdbg-mono);
	font-size: 13px;
	color: #374151;
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

/* Empty state — echoes the clients empty state */
.empty-state {
	padding: 48px 24px;
	text-align: center;
}

.empty-state-icon {
	font-size: 28px;
	color: #9ca3af;
	margin-bottom: 10px;
}

.empty-state-title {
	font-family: var(--jdbg-mono);
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 4px;
}

.empty-state-description {
	font-size: 13px;
	color: #6b7280;
	margin: 0;
}

.jdbg-pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 10px;
	border-top: 1px solid #f3f4f6;
}

.jdbg-page-info {
	font-family: var(--jdbg-mono);
	font-size: 12px;
	color: #6b7280;
}

/* Modals */
.jdbg-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.45);
	z-index: 1050;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
}

.jdbg-modal {
	background: #ffffff;
	border-radius: 12px;
	width: min(1100px, 100%);
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.jdbg-modal--narrow {
	width: min(560px, 100%);
}

.jdbg-modal--confirm {
	width: min(440px, 100%);
}

.jdbg-modal-head {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	padding: 14px 16px;
	border-bottom: 1px solid #f3f4f6;
	font-family: var(--jdbg-mono);
}

.jdbg-modal-sub {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

.jdbg-modal-body {
	padding: 14px 16px;
	overflow-y: auto;
}

.jdbg-modal-foot {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 12px 16px;
	border-top: 1px solid #f3f4f6;
}

.jdbg-section-title {
	font-family: var(--jdbg-mono);
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin: 12px 0 6px;
}

.jdbg-pre {
	background: #f9fafb;
	border: 1px solid #f3f4f6;
	border-radius: 10px;
	padding: 10px;
	font-family: var(--jdbg-mono);
	font-size: 12px;
	color: #111827;
	margin-bottom: 8px;
	white-space: pre-wrap;
	word-break: break-word;
}

.jdbg-scroll {
	max-height: 55vh;
	overflow-y: auto;
}

/* Config modal */
.config-channel {
	margin-bottom: 10px;
}

.config-switch {
	padding-top: 4px;
	padding-bottom: 4px;
	font-size: 14px;
}

.config-switch .form-check-label {
	cursor: pointer;
}

.config-key {
	font-family: var(--jdbg-mono);
	font-size: 11px;
	color: #6b7280;
	background: #f3f4f6;
	border-radius: 6px;
	padding: 1px 6px;
	margin-left: 6px;
}

.confirm-message {
	font-family: var(--jdbg-mono);
	font-size: 13px;
	color: #111827;
	margin: 6px 0 0;
	word-break: break-word;
}

/* Narrow screens: wrap filters, full-width search */
@media (max-width: 1100px) {
	.toolbar-right {
		width: 100%;
	}
	.search-input-wrapper {
		flex: 1;
	}
}
</style>
